import type { ComponentType, ReactNode } from "react"

import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"

type Tone = "info" | "warning" | "success" | "danger" | "muted"

const toneStyles: Record<Tone, { iconBg: string; iconText: string }> = {
  info: {
    iconBg: "bg-sky-100 dark:bg-sky-500/15",
    iconText: "text-sky-600 dark:text-sky-300",
  },
  warning: {
    iconBg: "bg-amber-100 dark:bg-amber-500/15",
    iconText: "text-amber-600 dark:text-amber-300",
  },
  success: {
    iconBg: "bg-emerald-100 dark:bg-emerald-500/15",
    iconText: "text-emerald-600 dark:text-emerald-300",
  },
  danger: {
    iconBg: "bg-red-100 dark:bg-red-500/15",
    iconText: "text-red-600 dark:text-red-300",
  },
  muted: {
    iconBg: "bg-muted",
    iconText: "text-muted-foreground",
  },
}

interface KpiCardProps {
  label: string
  value: ReactNode
  hint?: ReactNode
  icon: ComponentType<{ className?: string }>
  tone?: Tone
  className?: string
}

export function KpiCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = "muted",
  className,
}: KpiCardProps) {
  const style = toneStyles[tone]
  return (
    <Card className={className}>
      <CardContent className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {label}
          </p>
          <p className="text-2xl font-semibold tabular-nums">{value}</p>
          {hint ? (
            <p className="text-muted-foreground text-xs">{hint}</p>
          ) : null}
        </div>
        <div className={cn("rounded-lg p-2", style.iconBg)}>
          <Icon className={cn("size-5", style.iconText)} />
        </div>
      </CardContent>
    </Card>
  )
}
